import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { merge } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { Tag } from 'src/app/data/model/tag';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { TagsService } from 'src/app/data/service/tags.service';
import { ConfirmationDialogService } from 'src/app/shared/confirmation-dialog/confirmation-dialog.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { DocumentType } from '../../enums/document-type';
import { Invoice } from '../../models/invoice/invoice';
import { RelatedData, RelatedDataRequest, ApprovalUser, RelatedApprovals, Transaction } from '../../models/related/related';
import { ContactInvoiceShortData } from '../../models/related/related-contact';
import { TagToInvoices } from '../../models/tag/tag-to-invoice';
import { RelatedDataService } from '../../services/related-data.service';
import { UserService } from 'src/app/data/service/user.service';
import { TasksService } from 'src/app/data/service/tasks.service';
import { Task } from 'src/app/data/model/task';
import { TaskRequestService } from 'src/app/modules/tasks/request-task-modal/task-request.service';

@Component({
  selector: 'app-related',
  templateUrl: './related.component.html',
  styleUrls: ['./related.component.scss']
})
export class RelatedComponent extends BaseComponent implements OnInit {
  @Input() invoice: Invoice = null;
  @Input() supplierId: string = null;
  @Input() billsToPayMode: boolean = false;
  @Input() invoiceColorNumber: number = 1;
  @Input() hexColorClass: string = "";
  @Input() completedTasks: any[] = [];
  @Output() showContactForm = new EventEmitter<boolean>();
  @Output() clickInvoiceEvent = new EventEmitter<string>();
  @Output() clickInvoiceFormEvent = new EventEmitter<string>();

  isCompanyExpanded: boolean = true;
  isTransactionsExpanded: boolean = true;
  isAccountingExpanded: boolean = true;
  isMetadataExpanded: boolean = false;
  isTagsExpanded: boolean = true;
  isFormsExpanded: boolean = true;
  isVisibilityExpanded: boolean = true;
  hoveredUserIndex: number = -1;
  isUserListOpen: boolean = false;
  userSearchTerm: string = '';
  visibilityUsers: { name: string, userId?: string }[] = [];
  allOrgUsers: { name: string, email?: string, userId?: string }[] = [];
  filteredOrgUsers: { name: string, email?: string, userId?: string }[] = this.allOrgUsers;

  relatedData = {} as RelatedData;
  link: string = null;
  showToolTipDelay: number = 400;
  isSpinnerShown: boolean = false;
  tagList = [] as Tag[];
  filteredTagList = [] as Tag[];
  tagSearchTerm: string = null;
  organizationId: string = null;
  isApproving = false;
  isApprovalsExpanded = true;

  /** Approval requests on this bill (api/tasks/for-record), open first. */
  recordTasks: Task[] = [];
  recordTasksLoaded = false;
  isAddTagMenuItemShown: boolean = false;
  isBillsExpanded: boolean = true;
  outstandingInvoices = [] as ContactInvoiceShortData[];
  paidInvoices = [] as ContactInvoiceShortData[];
  outstandingTotal: number = null;
  isPaidInvoiceShown: boolean = false;
  filteredOutstandingInvoices: ContactInvoiceShortData[] = [];
  filteredPaidInvoices: ContactInvoiceShortData[] = [];
  activeStatusFilter: string = 'All'; // Changed from 'Authorised' to 'All'
  statusFilters = ['All', 'Awaiting Payment', 'Paid'];
  filteredTotal: number = 0;

  // Transactions placeholder data
  transactions: Transaction[] = [];

  private recordId: string = null;
  private recordTypeId: number = 1; // Set this appropriately for your use case

  xeroHistory: any[] = [];
  isXeroHistoryShown: boolean = false;
  isXeroHistoryLoading: boolean = false;

  constructor(
    private readonly _relatedDataService: RelatedDataService,
    private readonly _organizationService: OrganizationService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly _tagsService: TagsService,
    private readonly _suppliersService: SuppliersService,
    private readonly _router: Router,
    private readonly _userService: UserService, // <-- Inject UserService
    private readonly _tasksService: TasksService,
    private readonly _taskRequest: TaskRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isSpinnerShown = true;
    this.organizationId = this._organizationService.selectedOrganization;
    this.recordId = this.invoice?.id?.toString();

    const requestParam = {
      invoiceId: this.invoice?.id.toString(),
      invoiceReferenceId: this.invoice?.referenceId,
      organizationId: this._organizationService.selectedOrganization,
      supplierId: this.supplierId,
      userId: +localStorage.getItem('UserId')
    } as RelatedDataRequest;

    this._relatedDataService.getRelatedData(requestParam)
      .pipe(this.untilThis)
      .subscribe(
        (data: RelatedData) => {
          this.relatedData = data;
          this.isSpinnerShown = false;
          this.isTagsExpanded = this.relatedData.invoice?.tags?.length > 0;

          if (this.relatedData?.invoices?.length > 0) {
            // Load all invoices first
            this.outstandingInvoices = this.relatedData.invoices;
            this.paidInvoices = this.relatedData.invoices.filter(i => i.status === 'PAID');
            this.outstandingTotal = this.outstandingInvoices?.reduce((a, b) => a + b.amountDue, 0);

            // Apply initial filter
            this.filterBillsByStatus('All');
          }

          if (this.relatedData?.transactions?.length > 0) {
            this.transactions = this.relatedData.transactions;
          }

          this.prepareAccIntLink();
          this.loadRecordTasks();
        },
        (e: Error) => {
          this.isSpinnerShown = false;
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )

      this.getOrganizationTags();
      this.filterBillsByStatus('All'); // Changed from 'Authorised' to 'All'
      this.fetchAssignedUsers();

      // A request raised, approved or withdrawn anywhere -- here, the Tasks card, another tab.
      merge(
        this._tasksService.changed$.pipe(filter(c => !c.organizationId || c.organizationId === this.organizationId)),
        this._tasksService.refresh$
      ).pipe(debounceTime(300), this.untilThis).subscribe(() => this.loadRecordTasks());
  }

  fetchAssignedUsers() {
    if (!this.recordId || !this.recordTypeId) return;
    this._userService.getAssignedUsers(this.recordId, this.recordTypeId, true).subscribe((userIds: string[]) => {
      // Map assigned userIds to user objects from allOrgUsers (fetched via openUserList or cache)
      // If allOrgUsers is empty, fetch org users first
      if (!this.allOrgUsers.length) {
        this._userService.getUsersByOrganizationId(this.organizationId).subscribe((res) => {
          if (res && Array.isArray(res.data)) {
            this.allOrgUsers = res.data.map(u => ({
              name: u.name || u.fullName || u.firstName || u.email || 'User',
              email: u.email,
              userId: u.userId || u.id || u.guid || u.userGuidId
            }));
            this.visibilityUsers = this.allOrgUsers.filter(u => userIds.includes(u.userId));
          } else {
            this.allOrgUsers = [];
            this.visibilityUsers = [];
          }
        });
      } else {
        this.visibilityUsers = this.allOrgUsers.filter(u => userIds.includes(u.userId));
      }
    });
  }

  showInvoice(invoice: ContactInvoiceShortData) {
    if (this.billsToPayMode) {
      this.clickInvoiceEvent.emit(invoice.invoiceId);
    } else {
      this._router.navigate([`/payable/outstanding-amounts/${invoice.invoiceId}`], { replaceUrl: true });
    }
  }

  createNewTag() {
    this.isAddTagMenuItemShown = false;

    let tag = {
      backColor: null,
      textColor: null,
      tagName: this.tagSearchTerm
    } as Tag;

    tag.organizationId = this.organizationId;

    this._tagsService.createTag(tag)
      .pipe(take(1))
      .subscribe(
        (createdTag: Tag) => {
          tag = { ...createdTag };
          this.tagList = [...this.tagList, tag];
          this.filteredTagList = [...this.tagList];
          this.addTagToInvoices(tag);
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  addTagToInvoices(tag: Tag) {
    if (this.relatedData.invoice.tags?.some(t => t.tagId == tag.tagId)) {
      this._toasterMessageService.displayToasterState(ToasterTypes.Warning, 'Warning', 'The invoice has this tag!')
      return;
    }

    let tagToInvoices = {
      invoiceIds: [this.relatedData.invoice.invoiceId],
      tagId: tag.tagId,
      billsToPayMode: this.billsToPayMode
    } as TagToInvoices;

    this._tagsService.addTagToInvoices(tagToInvoices)
      .pipe(take(1))
      .subscribe(
        () => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', 'Tag added to selected invoices!');

          if (this.relatedData.invoice.tags?.length > 0) {
            this.relatedData.invoice.tags = [...this.relatedData.invoice.tags, tag];
          } else {
            this.relatedData.invoice.tags = [tag];
          }

          this.isTagsExpanded = true;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  deleteTagFromInvoice(tag: Tag) {
    this._tagsService.deleteTagFromInvoices(tag.tagId, this.invoice?.id.toString(), this.billsToPayMode)
      .pipe(this.untilThis)
      .subscribe(
        () => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', `Tag '${tag.tagName}' removed from the invoice!`);
          this.relatedData.invoice.tags = this.relatedData.invoice.tags.filter(t => t.tagId != tag.tagId);
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  filterTags(event) {
    this.tagSearchTerm = event;
    this.filteredTagList = this.tagList.filter(t => t.tagName.toLowerCase().includes(this.tagSearchTerm?.toLowerCase()));
    let exactlyMatchTags = this.filteredTagList.filter(t => t.tagName.toLowerCase() == this.tagSearchTerm?.toLowerCase());

    if (exactlyMatchTags?.length == 0) {
      this.isAddTagMenuItemShown = true;
    } else {
      this.isAddTagMenuItemShown = false;
    }
  }

  clearTagSearchValue() {
    setTimeout(() => {
      this.tagSearchTerm = null;
      this.filteredTagList = this.tagList;
    }, 250);
  }

  showContactPanel() {
    this.showContactForm.emit(true);
  }

  approveInvoice() {
    let invoiceid;
    if (this.relatedData?.invoice?.accIntInvoiceId != null) {
      invoiceid = this.relatedData?.invoice?.accIntInvoiceId;
    } else {
      invoiceid = this.relatedData?.invoice?.invoiceId;
    }
    const reportDataModel =
    {
      ApproveId: "",
      OrganizationId: this._organizationService.selectedOrganization,
      InvoiceId: invoiceid,
      ApproveBy: localStorage.getItem('UpUserId'),
      ApprovedByName: localStorage.getItem('UserName'),
      ApprovalDate: new Date()
    }

    this.isApproving = true;
    this._organizationService.InsertApprovals(reportDataModel) //checked
      .pipe(this.untilThis)
      .subscribe(data => {
        this.isApproving = false;
        if (data.status == 1) {
          this.ensureApprovals();
          this.relatedData.approvals.approved = true;
          this.relatedData.approvals.sentForApproval = false;
          this.relatedData.approvals.approvedAt = reportDataModel.ApprovalDate;
          this.relatedData.approvals.approvedBy = { name: reportDataModel.ApprovedByName } as ApprovalUser;
          this.isApprovalsExpanded = true;
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, '', data.message);
          this._tasksService.requestRefresh();
        } else {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', data.message || 'The bill could not be approved.');
        }
      },
      (e: Error) => {
        this.isApproving = false;
        this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
      });
  }

  /** The approval state, which the API leaves out entirely when there is none yet. */
  get approvals(): RelatedApprovals {
    return this.relatedData?.approvals;
  }

  private ensureApprovals(): void {
    if (this.relatedData && !this.relatedData.approvals) {
      this.relatedData.approvals = { approved: false, sentForApproval: false } as RelatedApprovals;
    }
  }

  private get recordTitle(): string {
    const who = this.relatedData?.company?.name || this.invoice?.supplierName;
    const number = this.invoice?.invoiceNumber;
    return [who, number].filter(x => !!x).join(' · ') || null;
  }

  get hasOpenRequest(): boolean {
    return this.recordTasks.some(t => t.status === 'open');
  }

  /** Approved, by whom and when: the approval record, or an approval just decided. */
  get approval(): { name: string; at: any } | null {
    if (this.approvals?.approved) {
      return { name: this.approvals.approvedBy?.name, at: this.approvals.approvedAt };
    }
    const done = this.recordTasks.find(t => t.status === 'completed' && t.outcome === 'approve' && !t.fields.length);
    return done ? { name: done.completedByName || done.assignedToName, at: done.completedAt } : null;
  }

  /** A request sent before approvals were tasks, which no task stands for. */
  get showsLegacyPending(): boolean {
    return this.recordTasksLoaded && !this.approval && !this.hasOpenRequest && !!this.approvals?.sentForApproval;
  }

  loadRecordTasks(): void {
    const ids = [this.invoice?.id, this.relatedData?.invoice?.invoiceId, this.relatedData?.invoice?.accIntInvoiceId]
      .filter(id => !!id).map(id => id.toString());
    if (!this.organizationId || !ids.length) { return; }

    this._tasksService.forRecord(this.organizationId, 'invoice', ids).pipe(take(1)).subscribe(
      tasks => {
        this.recordTasks = (tasks || []).filter(t => t.kind === 'approval' && t.status !== 'cancelled');
        this.recordTasksLoaded = true;
      },
      () => { this.recordTasksLoaded = true; });
  }

  requestApproval(): void {
    this.isApprovalsExpanded = true;
    this._taskRequest.open({
      organizationId: this.organizationId,
      recordType: 'invoice',
      recordId: (this.invoice?.id || this.relatedData?.invoice?.invoiceId)?.toString(),
      recordTitle: this.recordTitle
    }).then(created => {
      if (!created) { return; }
      this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Approval requested',
        created.created ? 'They\'ll find it on their Tasks card.' : 'That person has already been asked.');
      this.loadRecordTasks();
    });
  }

  assigneeLabel(task: Task): string {
    if (task.assignedToRole === 'admin') { return 'Any admin'; }
    return task.assignedToName || 'Someone';
  }

  /** "Account, tax rate · asked by Sam · 23 Sep · due 30 Sep". */
  requestLine(task: Task): string {
    const parts: string[] = [];
    parts.push(task.fields.length ? task.fields.map(f => f.label).join(', ') : 'Whole bill');
    if (task.assignedByName) { parts.push('asked by ' + this.firstName(task.assignedByName)); }
    parts.push(this.shortDate(task.createdAt));
    if (task.status === 'open' && task.dueDate) { parts.push('due ' + this.shortDate(task.dueDate)); }
    return parts.join(' · ');
  }

  taskPillClass(task: Task): string {
    if (task.status === 'open') { return task.isOverdue ? 'm-pill--danger' : 'm-pill--warning'; }
    return task.status === 'completed' ? 'm-pill--success' : task.status === 'rejected' ? 'm-pill--danger' : 'm-pill--neutral';
  }

  taskPillLabel(task: Task): string {
    if (task.status === 'open') { return task.isOverdue ? 'Overdue' : 'Pending'; }
    if (task.status === 'completed') { return 'Approved'; }
    return task.status.charAt(0).toUpperCase() + task.status.slice(1);
  }

  trackTask(_: number, task: Task): string {
    return task.taskId;
  }

  private firstName(name: string): string {
    return (name || '').trim().split(/\s+/)[0] || name;
  }

  /** Server dates are UTC without a zone; "23 Sep". */
  private shortDate(value: string): string {
    if (!value) { return ''; }
    const d = new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? value : value + 'Z');
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  menuClosed() {
    setTimeout(() => {
      this.isAddTagMenuItemShown = false;
    }, 100);
  }

  openUserList() {
    this.isUserListOpen = true;
    this.userSearchTerm = '';
    // Fetch users from API
    this._userService.getUsersByOrganizationId(this.organizationId).subscribe((res) => {
      if (res && Array.isArray(res.data)) {
        this.allOrgUsers = res.data.map(u => ({
          name: u.name || u.fullName || u.firstName || u.email || 'User',
          email: u.email,
          userId: u.userId || u.id || u.guid || u.userGuidId
        }));
        this.filteredOrgUsers = this.allOrgUsers;
      } else {
        this.allOrgUsers = [];
        this.filteredOrgUsers = [];
      }
    });
  }

  filterUserList(term: string) {
    this.filteredOrgUsers = this.allOrgUsers.filter(u => u.name.toLowerCase().includes(term.toLowerCase()));
  }

  addVisibilityUser(user: { name: string, userId: string }) {
    if (!this.visibilityUsers.some(u => u.userId === user.userId)) {
      const assignedBy = localStorage.getItem('UserId');
      this._userService.createAssignment({
        userId: user.userId,
        assignedBy,
        recordId: this.recordId,
        recordTypeId: this.recordTypeId,
        isCurrent: true
      }).subscribe(() => {
        this.fetchAssignedUsers();
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Success,
          'User Added',
          `${user.name} was given visibility to this record.`
        );
      });
    }
    this.isUserListOpen = false;
  }

  removeVisibilityUser(index: number, event: MouseEvent) {
    event.stopPropagation();
    const user = this.visibilityUsers[index];
    this._userService.updateAssignment({
      userId: user.userId,
      recordId: this.recordId,
      recordTypeId: this.recordTypeId,
      isCurrent: false
    }).subscribe(() => {
      this.fetchAssignedUsers();
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Success,
        'User Removed',
        `${user.name} no longer has visibility to this record.`
      );
    });
  }

  loadXeroHistory() {
    if (this.isXeroHistoryShown) {
      this.isXeroHistoryShown = false;
      return;
    }
    this.isXeroHistoryShown = true;
    this.isXeroHistoryLoading = true;
    const recordId = this.relatedData?.invoice?.accIntInvoiceId;
    const recordType = this.invoice?.type === DocumentType.Receipt ? 'BankTransaction'
      : this.invoice?.type === DocumentType['Credit Note'] ? 'CreditNote'
      : 'Invoice';
    this._suppliersService
      .getXeroHistory(this._organizationService.selectedOrganization, recordType, recordId)
      .pipe(take(1))
      .subscribe(
        (data) => {
          this.xeroHistory = data || [];
          this.isXeroHistoryLoading = false;
        },
        () => {
          this.isXeroHistoryLoading = false;
        }
      );
  }

  private prepareAccIntLink() {
    if (this.relatedData?.invoice?.accIntInvoiceId && this.relatedData?.organization?.isQBO) {
      this.link = `https://app.qbo.intuit.com/app/bill?txnId=${this.relatedData.invoice.referenceId}`;
    } else if (this.relatedData?.invoice?.accIntInvoiceId) {
      if (this.relatedData?.invoice?.type == 'ACCPAY') {
        this.link = `https://go.xero.com/organisationlogin/default.aspx?shortcode=${this.relatedData.organization.shortCode}&redirecturl=/AccountsPayable/Edit.aspx?InvoiceID=${this.relatedData.invoice.accIntInvoiceId}`;
      } else {
        this.link = `https://go.xero.com/organisationlogin/default.aspx?shortcode=${this.relatedData.organization.shortCode}&redirecturl=/AccountsPayable/ViewCreditNote.aspx?creditNoteID=${this.relatedData.invoice.accIntInvoiceId}`;
      }
    }
  }

  private getOrganizationTags() {
    this._tagsService.getTagsByOrganizationId(this.organizationId)
      .pipe(take(1))
      .subscribe(
        (tags: Tag[]) => {
          this.tagList = tags;
          this.filteredTagList = this.tagList;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  filterBillsByStatus(status: string | null) {
    this.activeStatusFilter = status;
    const allInvoices = this.relatedData?.invoices || [];

    if (!status || status === 'All') {
      this.filteredOutstandingInvoices = allInvoices;
    } else if (status === 'Awaiting Payment') {
      this.filteredOutstandingInvoices = allInvoices.filter(invoice =>
        invoice.status.toUpperCase() === 'AWAITING PAYMENT' ||
        invoice.status.toUpperCase() === 'AUTHORISED'
      );
    } else if (status === 'Paid') {
      this.filteredOutstandingInvoices = allInvoices.filter(invoice =>
        invoice.status.toUpperCase() === 'PAID'
      );
    }

    // Calculate total for filtered invoices
    this.filteredTotal = this.filteredOutstandingInvoices.reduce(
      (sum, invoice) => sum + invoice.amountDue,
      0
    );
  }

  getStatusCount(status: string): number {
    const allInvoices = this.relatedData?.invoices || [];

    if (!status || status === 'All') {
      return allInvoices.length;
    } else if (status === 'Awaiting Payment') {
      return allInvoices.filter(invoice =>
        invoice.status.toUpperCase() === 'AWAITING PAYMENT' ||
        invoice.status.toUpperCase() === 'AUTHORISED'
      ).length;
    } else if (status === 'Paid') {
      return allInvoices.filter(invoice =>
        invoice.status.toUpperCase() === 'PAID'
      ).length;
    }
    return 0;
  }

  openTaskDetailDialog(task){
    task.formName = task.name;
    this.clickInvoiceFormEvent.emit(task);
  }

  navigateToTransaction(transaction: Transaction) {
    const userId = (transaction.userId && transaction.userId !== '' && transaction.userId !== '00000000-0000-0000-0000-000000000000')
      ? transaction.userId
      : sessionStorage.Id;
    this._router.navigate(['/paperwork/missing-paperwork/' + transaction.reportId], {queryParams: { currentUserId: userId }});
  }
}
